import React, {useEffect, useMemo, useState} from 'react';

import {
  Box,
  Button,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {t} from 'i18next';

import {
  IPV4_REGEX,
  IPV6_REGEX,
  MAX_IP_ADDRESS_COUNT,
  POPUP_BUTTON_STYLE,
} from '../../../constants/constants';
import AurPopup from '../aur-popup/AurPopup';
import {replaceLocalePlaceholders} from '../../../utils/utils';
import AurButton from '../aur-button/AurButton';
import AurBanner from '../aur-banner/AurBanner';
type AurIpAddressProps = {
  ipAdressList?: string[];
  onIpListUpdate?: (value: string[]) => void;
  ipAddressEditState?: boolean;
  ipAddressError?: boolean;
};

export const AurIpAddress = (props: AurIpAddressProps) => {
  const {ipAdressList, onIpListUpdate, ipAddressEditState, ipAddressError} =
    props;
  const [ipAddressCsv, setIpAddressCsv] = useState<string>('');
  const [isError, setError] = useState<boolean>(false);
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [removeIndex, setRemoveIndex] = useState<number>();

  const [ipAddressList, setIpAddressList] = useState<string[]>([]);

  const isIP = (ip: string) => {
    return isIPv4(ip) || isIPv6(ip);
  };
  const isIPv4 = (ip: string) => {
    return IPV4_REGEX.test(ip);
  };
  const isIPv6 = (ip: string) => {
    return IPV6_REGEX.test(ip);
  };
  const addIpAddress = () => {
    const addressListUpdated = checkIsValidIpAddress();
    if (addressListUpdated) {
      setIpAddressList(addressListUpdated);
      setIpAddressCsv('');
    }
  };

  const checkIsValidIpAddress = () => {
    const ipAddressListAdded = ipAddressCsv
      .split(',')
      .map((item) => item.trim()) as string[];

    const isInValidString = ipAddressListAdded.some((ip) => {
      if (ip.indexOf('-') > -1) {
        const range = ip.split('-');
        return !isIP(range[0]) || !isIP(range[1]);
      }
      return !isIP(ip);
    });
    if (ipAddressCsv && isInValidString) {
      console.log('show error');
      setError(true);
      return null;
    } else {
      return Array.from(
        new Set([...ipAddressList, ...ipAddressListAdded])
      ).filter((item, index) => index < MAX_IP_ADDRESS_COUNT);
    }
  };

  useEffect(() => {
    if (onIpListUpdate) {
      onIpListUpdate(ipAddressList);
    }
  }, [ipAddressList]);
  //we want to restructure it
  useMemo(() => {
    if (ipAdressList) {
      ipAddressEditState
        ? setIpAddressList([])
        : setIpAddressList(ipAdressList || []);
      setError(false);
      ipAddressEditState && setIpAddressCsv(ipAdressList.toString());
    }
  }, [ipAdressList]);

  useEffect(() => {
    if (ipAddressError) setError(true);
  }, [ipAddressError]);
  const getIpAddressType = (ipAddress: string) => {
    let ipAddressToCheck = ipAddress;
    if (ipAddressToCheck.indexOf('-') > -1) {
      ipAddressToCheck = ipAddressToCheck.split('-')[0];
    }
    if (isIPv4(ipAddressToCheck)) {
      return t('common.ipAddress.ipv4');
    } else if (isIPv6(ipAddressToCheck)) {
      return t('common.ipAddress.ipv6');
    } else {
      return '';
    }
  };

  const cofirmRemoveIp = (index: number) => {
    if (index > -1) {
      setRemoveIndex(index);
      setModalOpen(true);
    }
  };

  // remove ip address from list
  const removeIp = () => {
    setIpAddressList((list) => list.filter((s, i) => i != removeIndex));
    setModalOpen(false);
  };

  return (
    <Box className="aur-ip-address">
      <Typography className="typo-text body2_size table-label">
        {t('common.ipAddress.title')}
      </Typography>
      {ipAddressList.length > 0 && (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="left">
                    <Typography className="typo-text subhead3_size">
                      {t('common.ipAddress.table.address')}
                    </Typography>
                  </TableCell>
                  <TableCell align="left">
                    <Typography className="typo-text subhead3_size">
                      {t('common.ipAddress.table.addressType')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography className="typo-text subhead3_size">
                      {t('common.ipAddress.table.actions')}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ipAddressList.map((ipAddress, index) => (
                  <TableRow key={index}>
                    <TableCell align="left">
                      <Typography className="typo-text body1_size">
                        {ipAddress}
                      </Typography>
                    </TableCell>
                    <TableCell align="left">
                      <Typography className="typo-text body1_size">
                        {getIpAddressType(ipAddress)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        className="trash-icon-btn"
                        variant="contained"
                        aria-label="Delete ip"
                        data-testid={`remove-ip-btn-${index + 1}`}
                        onClick={() => cofirmRemoveIp(index)}
                      >
                        <img src="/assests/images/trash.svg" alt="icon" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography className="typo-text body2_size add-another">
            {t('common.ipAddress.addAnother')}
          </Typography>
        </>
      )}
      {isError && (
        <AurBanner
          errorText={t('common.ipAddress.errorMessage')}
          errorType="error"
          showIcon={true}
        />
      )}
      <TextField
        helperText={t('common.ipAddress.inputHelper')}
        variant="filled"
        label={t('common.ipAddress.inputLabel')}
        value={ipAddressCsv}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setError(false);
          setIpAddressCsv(e.target.value);
        }}
        data-testid={'ip-address-input'}
        onBlur={checkIsValidIpAddress}
        error={!!ipAddressCsv && isError}
        InputProps={{
          disableUnderline: true,
          endAdornment: (
            <InputAdornment position="end">
              <AurButton
                classNames="typo-text"
                type="continue"
                size="Medium"
                onClick={addIpAddress}
                label={t('common.ipAddress.add')}
                dataTestId="add-ip-button"
                disabled={
                  !ipAddressCsv ||
                  isError ||
                  ipAddressList.length >= MAX_IP_ADDRESS_COUNT
                }
              />
            </InputAdornment>
          ),
        }}
      />

      <AurPopup
        open={isModalOpen}
        onOk={() => removeIp()}
        className="delete-popup-modal"
        onCancel={() => setModalOpen(false)}
        onClose={() => setModalOpen(false)}
        buttonStyle={POPUP_BUTTON_STYLE.SIDEBYSIDE}
        popupHeading={replaceLocalePlaceholders(
          'common.ipAddress.dialog.title',
          {
            ip: ipAddressList[removeIndex as number],
          }
        )}
        showPopupHeading={true}
        popupContent={replaceLocalePlaceholders(
          'common.ipAddress.dialog.description',
          {
            ip: ipAddressList[removeIndex as number],
          }
        )}
        labelActionCancel={t('common.ipAddress.dialog.cancel')}
        labelActionOk={t('common.ipAddress.dialog.confirm')}
        showCloseIcon={true}
      />
    </Box>
  );
};
